import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';
import { supabase } from '../lib/supabase';
import SeoMeta from '../components/SeoMeta';
import FinalistShareCard, { FinalistPreset } from '../components/FinalistShareCard';
import Countdown, { GALA_DATETIME } from '../components/Countdown';

interface NominationLite {
  id: string;
  category_name: string;
  category_type: 'project' | 'personal' | 'benchmark';
  status: string;
  candidate_name?: string | null;
  project_title?: string | null;
  institution?: string | null;
}

interface CategoryDuel {
  categoryName: string;
  categoryType: 'project' | 'personal' | 'benchmark';
  finalists: { id: string; title: string; institution?: string | null }[];
}

// Foto (candidaturas personales) o logo de la institución (candidaturas de
// proyecto), por nomination_id — más fiable que emparejar por nombre/
// institución en texto, que puede variar ligeramente entre candidaturas.
const LOGO_BY_NOMINATION_ID: Record<string, string> = {
  'a0f30ceb-7836-43cd-871f-ae30e58facd0': '/img/finalistas-2026/logos/amorebieta-etxano.png',
  '4996920e-f34b-4293-9588-ae8750ef1a56': '/img/finalistas-2026/logos/observatorio-fraude-sanidad.png',
  '10ec992e-c9ff-4625-8f62-f09f226d441a': '/img/finalistas-2026/logos/iaap.jpg',
  '6974c8ee-2f57-4076-907f-d24aba452007': '/img/finalistas-2026/logos/iaap.jpg',
  '070c00c7-c505-4fe3-93e6-cd7eec648d42': '/img/finalistas-2026/logos/ctti-generalitat.png',
  'ae8e7ac1-ce74-41e5-9b63-34c7d28c4b35': '/img/finalistas-2026/logos/ministerio-presidencia-justicia.webp',
  '263c1009-2dcb-42cb-9a54-c240d52ffc1e': '/img/finalistas-2026/logos/iaap.jpg',
  '9f7f400e-26e8-413d-bf8d-09cd5b5e0049': '/img/finalistas-2026/logos/iaap.jpg',
  'a93185d5-fe2c-48dc-a226-a2884a318b9b': '/img/finalistas-2026/logos/sateg-guanajuato.png',
  'fbf0f88a-05e4-4f52-9e3b-7e1ef0e3fc72': '/img/finalistas-2026/logos/siga-chiapas.png',
  'fbd36f9d-6034-4d6d-87ec-54cb86231a32': '/img/finalistas-2026/logos/miterd.jpg',
  '03a99fec-5a24-4e4d-ba5a-c81f23aa1fc5': '/img/finalistas-2026/logos/diputacio-barcelona.png',
  '75efb1bb-8cc2-44f8-be28-86215772fbdf': '/img/finalistas-2026/logos/ministerio-presidencia-justicia.webp',
  '8b83ef3b-96c3-4388-b1bb-9a46cf66e205': '/img/finalistas-2026/logos/diputacio-barcelona.png',
};

const PHOTO_BY_NOMINATION_ID: Record<string, string> = {
  '7977cb29-4be5-4f0e-97de-111699568290': '/img/finalistas-2026/personas/carmen-maria-martinez-morales.jpg',
  '17ecfe97-df09-4ada-ab1c-d41b46f82971': '/img/finalistas-2026/personas/maria-del-mar-herrera-menchen.jpg',
  'e61335d7-361f-4dc6-9ac8-6e7bb9651bde': '/img/finalistas-2026/personas/consuelo-doncel-rodriguez.png',
  '3ed1ec92-bf52-4431-8a16-f6042f640e1a': '/img/finalistas-2026/personas/cristina-moreno-acosta.jpg',
  '05112327-1bd9-4569-abaf-2a10ccfd1046': '/img/finalistas-2026/personas/jose-antonio-arias-lombardero.webp',
  'd4e5b825-bce4-4df8-b3d9-1ce6cfe3b169': '/img/finalistas-2026/personas/juan-carlos-santamarta-cerezal.jpg',
  '25a57c9f-7eb7-453d-9f67-c8bcbc895fb2': '/img/finalistas-2026/personas/guillermo-eduardo-cisneros-gutierrez.webp',
  'c88cecfd-7d89-441c-bec9-c990fdf2ac91': '/img/finalistas-2026/personas/luz-felix.png',
};

const TYPE_DOT: Record<string, string> = {
  project: '#ffd702',
  personal: '#7fd1ff',
  benchmark: '#b98bff',
};
const TYPE_ORDER: Record<string, number> = { project: 0, personal: 1, benchmark: 2 };
const isLatam = (name: string) => name.toUpperCase().includes('LATAM');

const titleOf = (n: NominationLite) =>
  n.category_type === 'personal'
    ? n.candidate_name || 'Candidatura sin nombre'
    : n.project_title || 'Candidatura sin título';

// Solo el nombre, sin la categoría delante, y recortado a partir de la
// quinta palabra: en el desplegable "Selecciona finalista" los títulos de
// proyecto largos hacían la lista ilegible, sobre todo en móvil.
const shortLabel = (text: string, maxWords = 5) => {
  const words = text.trim().split(/\s+/);
  if (words.length <= maxWords) return text.trim();
  return `${words.slice(0, maxWords).join(' ')}…`;
};

const FinalistTile: React.FC<{
  id: string;
  title: string;
  institution?: string | null;
}> = ({ id, title, institution }) => {
  const photo = PHOTO_BY_NOMINATION_ID[id];
  const logo = LOGO_BY_NOMINATION_ID[id];

  return (
    <div className="finaltwo-finalist">
      {photo && <img className="finaltwo-finalist-photo" src={photo} alt="" />}
      {!photo && logo && <img className="finaltwo-finalist-logo" src={logo} alt="" />}
      <span className="finaltwo-finalist-name" title={title}>{title}</span>
      {!photo && !logo && institution && <span className="finaltwo-finalist-inst">{institution}</span>}
    </div>
  );
};

const FinalTwo: React.FC = () => {
  const [duels, setDuels] = useState<CategoryDuel[] | null>(null);
  const [error, setError] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const carouselRef = useRef<HTMLDivElement | null>(null);
  const slideRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [finalTwoRes, nomsRes] = await Promise.all([
        supabase.schema('premios').rpc('get_public_final_two'),
        supabase.schema('premios').rpc('get_public_nominations'),
      ]);
      if (cancelled) return;
      if (finalTwoRes.error || nomsRes.error || !finalTwoRes.data || !nomsRes.data) {
        console.error('Error cargando los 2 últimos finalistas:', finalTwoRes.error || nomsRes.error);
        setError(true);
        return;
      }

      const pairs = finalTwoRes.data as { category_id: string; nomination_id: string }[];
      const noms = nomsRes.data as NominationLite[];
      const nomsById = new Map(noms.map((n) => [n.id, n]));

      const byCategory = new Map<string, CategoryDuel>();
      pairs.forEach(({ nomination_id }) => {
        const nom = nomsById.get(nomination_id);
        if (!nom) return;
        const key = nom.category_name;
        if (!byCategory.has(key)) {
          byCategory.set(key, { categoryName: nom.category_name, categoryType: nom.category_type, finalists: [] });
        }
        byCategory.get(key)!.finalists.push({ id: nom.id, title: titleOf(nom), institution: nom.institution });
      });

      // Orden neutro (alfabético por título) dentro de cada categoría: no debe
      // insinuar en ningún caso quién quedó 1º y quién 2º en la votación real.
      const list = Array.from(byCategory.values())
        .filter((d) => d.finalists.length === 2)
        .map((d) => ({ ...d, finalists: [...d.finalists].sort((a, b) => a.title.localeCompare(b.title, 'es')) }))
        .sort((a, b) => {
          if (TYPE_ORDER[a.categoryType] !== TYPE_ORDER[b.categoryType]) {
            return TYPE_ORDER[a.categoryType] - TYPE_ORDER[b.categoryType];
          }
          const la = isLatam(a.categoryName);
          const lb = isLatam(b.categoryName);
          if (la !== lb) return la ? 1 : -1;
          return a.categoryName.localeCompare(b.categoryName, 'es');
        });

      setDuels(list);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // El carrusel avanza el punto activo según qué tarjeta ocupa el centro del
  // scroll, así los puntos y las flechas quedan sincronizados también cuando
  // se desliza a mano (táctil o trackpad), no solo al pulsar las flechas.
  useEffect(() => {
    if (!duels || duels.length === 0) return;
    const root = carouselRef.current;
    if (!root) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.6) {
            const idx = Number((entry.target as HTMLElement).dataset.index);
            if (!Number.isNaN(idx)) setActiveIndex(idx);
          }
        });
      },
      { root, threshold: [0.6] }
    );
    slideRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [duels]);

  const scrollToIndex = (i: number) => {
    if (!duels) return;
    const clamped = Math.max(0, Math.min(duels.length - 1, i));
    slideRefs.current[clamped]?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') scrollToIndex(activeIndex + 1);
      else if (e.key === 'ArrowLeft') scrollToIndex(activeIndex - 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex, duels]);

  if (error) {
    return (
      <div className="informe-page">
        <div className="informe-page-content" style={{ textAlign: 'center', paddingTop: 80 }}>
          <p>No se ha podido cargar la información. Inténtalo de nuevo en unos instantes.</p>
          <Link to="/" className="wrapped-btn primary" style={{ marginTop: 16 }}>
            Volver al inicio
          </Link>
        </div>
      </div>
    );
  }

  if (!duels) {
    return (
      <div className="informe-page">
        <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 120 }}>
          <div className="wrapped-spinner" />
        </div>
      </div>
    );
  }

  const categoryNames = duels.map((d) => d.categoryName);
  const presets: FinalistPreset[] = duels.flatMap((d) =>
    d.finalists.map((f) => ({
      id: f.id,
      label: shortLabel(f.title),
      name: f.title,
      category: d.categoryName,
      photoUrl: PHOTO_BY_NOMINATION_ID[f.id] || LOGO_BY_NOMINATION_ID[f.id],
      photoKind: PHOTO_BY_NOMINATION_ID[f.id] ? 'photo' : 'logo',
    }))
  );

  return (
    <div className="informe-page">
      <SeoMeta
        title="Los 2 últimos finalistas · Premios NovaGob Excelencia 2026"
        description="Descubre, categoría a categoría, las 2 candidaturas que llegan a la gala final de los Premios NovaGob Excelencia 2026. El ganador se revela el 10 de diciembre."
        path="/finalistas-2026"
      />
      <header className="informe-page-header">
        <Link to="/" className="informe-page-back">
          <ArrowLeft size={18} /> Volver
        </Link>
        <span className="informe-page-title">Los 2 últimos finalistas · Edición 2026</span>
        <Link to="/" className="informe-page-home">
          Volver al inicio
        </Link>
      </header>

      <div className="informe-page-content" style={{ maxWidth: 1500 }}>
        <section className="finaltwo-hero">
          <span className="finaltwo-hero-kicker">
            <Sparkles size={14} /> Votación pública cerrada
          </span>
          <h1 className="finaltwo-hero-title">Ya solo quedan 2 por categoría</h1>
          <p className="finaltwo-hero-sub">
            De las 3 candidaturas finalistas, estas 2 llegan a la gala del <strong>10 de diciembre</strong>. Esa
            noche conoceremos quién es el ganador.
          </p>
          <Countdown target={GALA_DATETIME} doneLabel="¡La gala ya ha empezado!" />
        </section>

        {duels.length === 0 ? (
          <div className="card" style={{ marginTop: 24, textAlign: 'center', padding: 40 }}>
            <p style={{ color: 'var(--color-text-secondary)' }}>
              Todavía no hay categorías con resultado. Vuelve en unos instantes.
            </p>
          </div>
        ) : (
          <>
            <div className="finaltwo-carousel-wrap">
              <button
                type="button"
                className="finaltwo-nav-arrow left"
                onClick={() => scrollToIndex(activeIndex - 1)}
                disabled={activeIndex === 0}
                aria-label="Categoría anterior"
              >
                <ChevronLeft size={22} />
              </button>

              <div className="finaltwo-carousel" ref={carouselRef}>
                {duels.map((duel, i) => (
                  <div
                    className="finaltwo-slide"
                    key={duel.categoryName}
                    data-index={i}
                    ref={(el) => {
                      slideRefs.current[i] = el;
                    }}
                  >
                    <div className="finaltwo-slide-card">
                      <div className="finaltwo-card-header">
                        <span className="informe-catcard-dot" style={{ background: TYPE_DOT[duel.categoryType] }} />
                        <span className="finaltwo-card-category">{duel.categoryName}</span>
                      </div>
                      <div className="finaltwo-duel">
                        <FinalistTile
                          id={duel.finalists[0].id}
                          title={duel.finalists[0].title}
                          institution={duel.finalists[0].institution}
                        />
                        <span className="finaltwo-vs">&amp;</span>
                        <FinalistTile
                          id={duel.finalists[1].id}
                          title={duel.finalists[1].title}
                          institution={duel.finalists[1].institution}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <button
                type="button"
                className="finaltwo-nav-arrow right"
                onClick={() => scrollToIndex(activeIndex + 1)}
                disabled={activeIndex === duels.length - 1}
                aria-label="Categoría siguiente"
              >
                <ChevronRight size={22} />
              </button>
            </div>

            <div className="finaltwo-dots">
              {duels.map((duel, i) => (
                <button
                  key={duel.categoryName}
                  type="button"
                  className={`finaltwo-dot${i === activeIndex ? ' active' : ''}`}
                  onClick={() => scrollToIndex(i)}
                  aria-label={`Ir a ${duel.categoryName}`}
                />
              ))}
            </div>
          </>
        )}

        <section className="finaltwo-share">
          <h3>¿Eres uno de los 2 últimos finalistas?</h3>
          <p style={{ marginBottom: 18 }}>
            Elige tu nombre y te preparamos la imagen al momento, lista para compartir en tus redes con el
            #PremiosNovaGob2026. Si prefieres otra foto, puedes cambiarla.
          </p>
          <FinalistShareCard
            pillText="ENTRE LOS 2 ÚLTIMOS"
            categories={categoryNames}
            fileSuffix="top2-finalista"
            uploadHint="Sube tu foto"
            presets={presets}
          />
        </section>
      </div>
    </div>
  );
};

export default FinalTwo;
